class HeaderMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.openTitle = null;
    this.triggers = [];
    this.panels = [];
    this.panelByTitle = new Map();
    this.header = null;

    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-mega-menu-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-mega-menu-panel]'));
    if (!this.triggers.length || !this.panels.length) return;

    this.panelByTitle = new Map(this.panels.map((panel) => [panel.dataset.megaMenuPanel, panel]));
    this.header = this.closest('[data-header]');

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        this.toggle(trigger.dataset.megaMenuTrigger);
      });
    });

    // Clicks inside an open panel must not close it.
    this.panels.forEach((panel) => {
      panel.addEventListener('click', (event) => event.stopPropagation());
    });

    this.setupProductsHover();

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
  }

  onDocumentClick() {
    this.close();
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  close() {
    if (this.openTitle === null) return;
    const panel = this.panelByTitle.get(this.openTitle);
    if (panel) this.hidePanel(panel);
    const trigger = this.triggers.find((item) => item.dataset.megaMenuTrigger === this.openTitle);
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.openTitle = null;
    this.header?.classList.remove('header--menu-open');
  }

  open(title) {
    this.close();
    const panel = this.panelByTitle.get(title);
    if (!panel) return;
    this.showPanel(panel);
    const trigger = this.triggers.find((item) => item.dataset.megaMenuTrigger === title);
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    this.openTitle = title;
    this.header?.classList.add('header--menu-open');
  }

  showPanel(panel) {
    if (this.closeTimers && this.closeTimers.has(panel)) {
      clearTimeout(this.closeTimers.get(panel));
      this.closeTimers.delete(panel);
    }
    panel.hidden = false;
    // Force reflow so the transition runs from the hidden state.
    void panel.offsetWidth;
    panel.classList.add('is-open');
  }

  hidePanel(panel) {
    panel.classList.remove('is-open');
    if (!this.closeTimers) this.closeTimers = new Map();
    const timer = setTimeout(() => {
      panel.hidden = true;
      this.closeTimers.delete(panel);
    }, 450);
    this.closeTimers.set(panel, timer);
  }

  toggle(title) {
    if (this.openTitle === title) {
      this.close();
      return;
    }
    this.open(title);
  }

  // Level-1 sticky hover within products mega menus.
  setupProductsHover() {
    this.panels
      .filter((panel) => panel.dataset.megaMenuType === 'products')
      .forEach((panel) => {
        const l1Triggers = Array.from(panel.querySelectorAll('[data-mega-menu-l1-trigger]'));
        const l2Panels = Array.from(panel.querySelectorAll('[data-mega-menu-l2-panel]'));

        const activate = (index) => {
          l1Triggers.forEach((trigger, i) => {
            const isActive = i === index;
            trigger.classList.toggle('is-active', isActive);
            trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
          });
          l2Panels.forEach((l2Panel, i) => {
            l2Panel.classList.toggle('is-active', i === index);
          });
        };

        l1Triggers.forEach((trigger, index) => {
          trigger.addEventListener('mouseenter', () => activate(index));
          trigger.addEventListener('focus', () => activate(index));
        });
      });
  }
}

customElements.define('header-mega-menu', HeaderMegaMenu);

class HeaderMenuDrawer extends HTMLElement {
  constructor() {
    super();
    this.panel = null;
    this.openers = [];
    this.onKeydown = this.onKeydown.bind(this);
  }

  connectedCallback() {
    this.panel = this.querySelector('.menu-drawer__panel');
    this.openers = Array.from(document.querySelectorAll('[data-menu-drawer-opener]'));

    this.openers.forEach((opener) => {
      opener.addEventListener('click', () => this.open());
    });

    this.querySelectorAll('[data-menu-drawer-close]').forEach((el) => {
      el.addEventListener('click', () => this.close());
    });

    this.setupAccordions();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
  }

  open() {
    // Keep the announcement bar visible: drawer starts at the header's top edge.
    const announcement = document.querySelector('.announcement-bar');
    const top = announcement ? announcement.getBoundingClientRect().bottom : 0;
    this.style.setProperty('--menu-drawer-top', `${Math.max(top, 0)}px`);

    this.hidden = false;
    // Force reflow so the slide-in transition runs from the hidden state.
    void this.offsetWidth;
    this.classList.add('is-open');
    document.body.classList.add('menu-drawer-open');
    this.openers.forEach((opener) => opener.setAttribute('aria-expanded', 'true'));
    document.addEventListener('keydown', this.onKeydown);
  }

  close() {
    this.classList.remove('is-open');
    document.body.classList.remove('menu-drawer-open');
    this.openers.forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
    document.removeEventListener('keydown', this.onKeydown);

    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      this.hidden = true;
    }, 400);
  }

  onKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  setupAccordions() {
    this.querySelectorAll('[data-menu-drawer-accordion-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const accordion = toggle.closest('[data-menu-drawer-accordion]');
        if (!accordion) return;
        const isOpen = accordion.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      });
    });
  }
}

customElements.define('header-menu-drawer', HeaderMenuDrawer);
